import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { appEnv } from '../../app.config';
import { catchError, empty, finalize, Observable, of, shareReplay, Subject, switchMap, tap } from 'rxjs';
import { ResponseModel } from '../models/response.model';
import { CookieService } from 'ngx-cookie-service';
import { NotyfService } from './notyf.service';

export type Auth = {id: number, uuid: string, fullName: string, subName: string, type: number, email: string, roleId: number, isPremiumAccount: boolean, isAdminOrigin: boolean, signInAsEmail: string, isCustmAccount: boolean, signInAsName: string, adminUserUuid: string};
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public auth: any;
  public authData: any;
  public isEcommerce: boolean = false;
  private _auth: Auth = {} as Auth;

  private _authCPState = signal<Auth>({} as Auth);
  authCPState = this._authCPState.asReadonly();

  private _isValidAuthEcommerce = signal<boolean>(false);
  readonly isValidAuthEcommerce = this._isValidAuthEcommerce.asReadonly();

  private refreshToken$?: Observable<any>;
  constructor(
    private jwtHelper: JwtHelperService,
    private http: HttpClient,
    private cookieService: CookieService,
    private notyf: NotyfService
  ) { }

  public get authInfo () {
    return this._auth;
  }

  /** The signed-in admin user's id - from login (setAuth), or the custm_token's
   *  sub claim after a page reload. (authInfo is the e-commerce portal's
   *  customer sign-in, empty for admin staff.) */
  public get currentUserId (): number | null {
    if (this.authData?.id) return Number(this.authData.id) || null;
    try {
      const token = localStorage.getItem('custm_token');
      return token ? Number(this.jwtHelper.decodeToken(token)?.sub) || null : null;
    } catch {
      return null;
    }
  }

  /** Display name of the currently signed-in admin user (from the custm_token's
   *  user_details, same source app.component uses for the top-right name badge). */
  public get currentUserName (): string {
    try {
      const details = JSON.parse(localStorage.getItem('user_details') ?? 'null');
      const name = [details?.first_name, details?.last_name].filter(Boolean).join(' ').trim();
      return name || 'Unknown user';
    } catch {
      return 'Unknown user';
    }
  }

  setAuth (token: any = null) {
    const decoded = this.jwtHelper.decodeToken(token);
    const authDetails = decoded.user_details;
    localStorage.setItem('custm_token', token);
    localStorage.setItem('user_details', JSON.stringify(authDetails));
    this.authData = {
      id: Number(decoded.sub),
      details: authDetails,
      roleId: authDetails.role_id ?? 3
    }
  }

  decodedToken (token: string|null = null) {
    const custmToken = token || localStorage.getItem('custm_token');
    if (!custmToken) return [];
    return this.jwtHelper.decodeToken(custmToken);
  }

  removeAuth () {
    localStorage.removeItem('custm_token');
    localStorage.removeItem('user_details');
  }

  authCheck () {
    return (!this.jwtHelper.isTokenExpired()) ? true : false;
  }

  signInMs (data: {email: string}): Observable<ResponseModel> {
    return this.http.get<ResponseModel>(`${appEnv.api}oauth?email=${data.email}`, { params: {isLogin: 1}});
  }

  validateMSlogin (code: any = ''): Observable<ResponseModel> {
    return this.http.get<ResponseModel>(`${appEnv.api}oauth/callback?code=${code}`);
  }

  signOutMs (): Observable<any> {
    return this.http.get<any>(`${appEnv.api}oauth/logout`);
  }

  refreshToken() {
    if (this.refreshToken$) {
      return this.refreshToken$;
    }

    this.refreshToken$ = this.http.get<any>(`${appEnv.api}oauth/refresh-token`).pipe(
      tap(res => this.setAuth(res.token)),
      shareReplay(1),
      finalize(() => this.refreshToken$ = undefined)
    );

    this.refreshToken$.subscribe({
      error: (err) => {
        console.error('Token refresh failed', err);
      }
    });

    return this.refreshToken$;
  }

  validateToken (): Observable<any> {
    return this.http.get<any>(`${appEnv.api}oauth/validate-token`);
  }

  public get authDetails () {
    return this.authData;
  }


  //Ecommerce authentication
  authCustomerVerificationCode (code: string = '', uuid: string = '', isAdmin: number = 0, email: string = ''): Observable<any> {
    const payloads = {code,branch:uuid};
    if (isAdmin) {
      Object.assign(payloads, {
        isAdmin,
        email
      });
    }
    return this.http.post<any>(`${appEnv.api}ecommerce/auth/validate-code`, payloads);
  }

  setEcommerceContent () {
    this.isEcommerce = true;
  }

  authCheckEcommerce () {
    const token:string|null = localStorage.getItem('ec_token') ?? null;
    if (!token) return false;
    return !this.jwtHelper.isTokenExpired(token);
  }

  setEcommerceModels () {
    this.setEcommerceContent();
    const token = localStorage.getItem('ec_token');
    if (token) {
      const decodedToken = this.jwtHelper.decodeToken(token);
      this._auth = {
        ...this._auth,
        id: Number(decodedToken.sub),
        uuid: decodedToken.uuid,
        fullName: decodedToken.pointOfContact,
        subName: decodedToken.branchName,
        type: decodedToken.type,
        email: decodedToken.email,
        signInAsEmail: decodedToken.signInAsEmail,
        roleId: decodedToken.roleId,
        isPremiumAccount: decodedToken.isPremiumAccount,
        isAdminOrigin: decodedToken.isAdminOrigin,
        isCustmAccount: decodedToken.isCustmAccount,
        signInAsName: decodedToken.signInAsName,
        adminUserUuid: decodedToken?.adminUuid ?? null
      }

      this._authCPState.set(this._auth);
      this._isValidAuthEcommerce.set(true);
    }
  }

  signOutEcommerce () {
    this._isValidAuthEcommerce.set(false);
    localStorage.removeItem('ec_token');
  }

  switchToPortal () {
    this.notyf.notifySuccess('Hang on! we\'re preparing your profile for Customer-Portal.');
    this.http.get<any>(`${appEnv.api}ecommerce/auth/switch-panel-to-portal`).subscribe({
      next: response => {
        localStorage.setItem('ec_token', response.token);
        window.location.href = '/ecommerce/profile-overview';
      }, error: error => {
        this.notyf.notifyError('Failed to authenticate this account for Customer-portal')
      }
    });
  }

  getTokenExpirationDate (token: string) {
    return this.jwtHelper.getTokenExpirationDate(token) || '';
  }

  adminToken () {
    return localStorage.getItem('custm_token') || '';
  }
}
